import { TenFourText } from '@/components/TenFourText'

import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { LiquidGlassDefs } from './LiquidGlassDefs';
import styles from './Toast.module.css';

interface Toast {
  id: string;
  type: 'success' | 'error' | 'info';
  message: string;
  group?: string;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (type: Toast['type'], message: string, group?: string) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const addToast = useCallback((type: Toast['type'], message: string, group?: string) => {
    setToasts((prev) => {
      // If this toast has a group, replace any existing toast in that group
      if (group) {
        const existing = prev.find((t) => t.group === group);
        if (existing) {
          // Clear old timer
          const oldTimer = timersRef.current.get(existing.id);
          if (oldTimer) clearTimeout(oldTimer);
          // Set new timer
          const timer = setTimeout(() => {
            setToasts((p) => p.filter((t) => t.id !== existing.id));
            timersRef.current.delete(existing.id);
          }, 3000);
          timersRef.current.set(existing.id, timer);
          // Replace in place
          return prev.map((t) => t.id === existing.id ? { ...t, type, message } : t);
        }
      }

      // If exact same type+message exists (no group), reset timer
      const exactMatch = prev.find((t) => t.type === type && t.message === message && !t.group);
      if (exactMatch) {
        const oldTimer = timersRef.current.get(exactMatch.id);
        if (oldTimer) clearTimeout(oldTimer);
        const timer = setTimeout(() => {
          setToasts((p) => p.filter((t) => t.id !== exactMatch.id));
          timersRef.current.delete(exactMatch.id);
        }, 4000);
        timersRef.current.set(exactMatch.id, timer);
        return prev;
      }

      // New toast
      const id = Math.random().toString(36).substring(7);
      const timer = setTimeout(() => {
        setToasts((p) => p.filter((t) => t.id !== id));
        timersRef.current.delete(id);
      }, 4000);
      timersRef.current.set(id, timer);
      return [...prev, { id, type, message, group }];
    });
  }, []);

  const removeToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) clearTimeout(timer);
    timersRef.current.delete(id);
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      <LiquidGlassDefs id="toastRefract" />
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
}

function ToastContainer({
  toasts,
  onRemove,
}: {
  toasts: Toast[];
  onRemove: (id: string) => void;
}) {
  if (toasts.length === 0) return null;

  return (
    <div className={styles.container}>
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role={toast.type === 'error' ? 'alert' : 'status'}
          aria-atomic="true"
          className={[
            styles.toast,
            toast.type === 'success' ? styles.success : '',
            toast.type === 'error' ? styles.error : '',
            toast.type === 'info' ? styles.info : '',
          ].join(' ')}
        >
          <span className={styles.icon} aria-hidden="true">
            {toast.type === 'success' && <CheckCircle size={20} className={styles.iconSuccess} />}
            {toast.type === 'error' && <AlertCircle size={20} className={styles.iconError} />}
            {toast.type === 'info' && <Info size={20} className={styles.iconInfo} />}
          </span>

          <p
            className={[
              styles.message,
              toast.type === 'success' ? styles.messageSuccess : '',
              toast.type === 'error' ? styles.messageError : '',
              toast.type === 'info' ? styles.messageInfo : '',
            ].join(' ')}
          >
            <TenFourText>{toast.message}</TenFourText>
          </p>

          <button type="button" onClick={() => onRemove(toast.id)} className={styles.closeBtn} aria-label="Dismiss notification">
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}
